import type { FC, ReactNode } from 'react';
import {
  OnboardingStepper,
  type Step,
  type StepperVariant,
} from './OnboardingSteps';

export type OnboardingLayoutProps = {
  currentStep: Step | (string & {});
  variant?: StepperVariant;
  logo?: ReactNode;
  stepper?: ReactNode;
  children: ReactNode;
};

export const OnboardingLayout: FC<OnboardingLayoutProps> = ({
  currentStep,
  variant = 'dark',
  logo,
  stepper,
  children,
}) => {
  const bgClass = variant === 'light' ? 'bg-neutral-50' : 'bg-[#130e30]';

  return (
    <div className={`h-full ${bgClass} flex flex-col`}>
      {/* Header with optional logo and stepper */}
      <div className="shrink-0 pt-8 pb-6">
        {logo && <div className="absolute top-8 left-10">{logo}</div>}

        <div className="flex justify-center">
          {stepper ?? (
            <OnboardingStepper
              currentStep={currentStep as Step}
              variant={variant}
            />
          )}
        </div>
      </div>

      {/* Content area */}
      {children}
    </div>
  );
};

export type OnboardingCardProps = {
  width?: 'narrow' | 'wide' | 'extra-wide';
  fill?: boolean;
  header: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
};

const WIDTH_CLASSES = {
  narrow: 'w-[445px]',
  wide: 'w-[779px]',
  'extra-wide': 'w-[850px]',
};

export const OnboardingCard: FC<OnboardingCardProps> = ({
  width = 'narrow',
  fill = false,
  header,
  children,
  footer,
}) => {
  const widthClass = WIDTH_CLASSES[width];

  return (
    <div className="flex-1 flex items-center justify-center pb-8 min-h-0">
      <div
        className={`${widthClass} bg-white rounded-2xl border border-neutral-200 shadow-lg flex flex-col max-h-full ${fill ? 'h-full' : ''}`}
      >
        {/* Header */}
        <div className="px-6 py-4 flex flex-col gap-0.5 shrink-0">{header}</div>

        {/* Separator */}
        <div className="h-px bg-neutral-300 shrink-0" />

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-y-auto">{children}</div>

        {/* Footer */}
        {footer && (
          <>
            <div className="h-px bg-neutral-300 shrink-0" />
            <div className="px-6 py-3 shrink-0">{footer}</div>
          </>
        )}
      </div>
    </div>
  );
};
